
import React from 'react';
import { cn } from "@/lib/utils";
import { DealersMap } from "@/components/DealersMap";
import { DealersListView } from "@/components/dealers/DealersListView";
import { DealersFilter } from "@/components/dealers/DealersFilter";
import { DealersList } from "@/components/map/DealersList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { List, Map } from "lucide-react";
import { Dealer } from "@/types/dealer";
import { DealerFilterState } from "@/hooks/dealers/types";
import { useGeolocation } from "@/hooks/useGeolocation";

interface DealersContainerProps {
  dealers: Dealer[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  totalCount?: number;
  filters: DealerFilterState;
  view: "list" | "map";
  cities: string[];
  enableInfiniteScroll?: boolean;
  onViewChange: (view: "list" | "map") => void;
  onUpdateFilters: (filters: Partial<DealerFilterState>) => void;
  onDealerClick: (dealerId: string) => void;
  onRefresh: () => void;
  onLoadMore?: () => void;
  onCityClick?: (city: string) => void;
  className?: string;
}

export function DealersContainer({
  dealers,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  totalCount = 0,
  filters,
  view,
  cities,
  enableInfiniteScroll = false,
  onViewChange,
  onUpdateFilters,
  onDealerClick,
  onRefresh,
  onLoadMore,
  onCityClick,
  className
}: DealersContainerProps) {
  const { position } = useGeolocation({
    showSuccessToast: false
  });

  return (
    <div className={cn("w-full", className)}>
      {cities && cities.length > 0 && (
        <div className="bg-card rounded-lg shadow-sm p-4 mb-4">
          <h2 className="text-lg font-medium mb-2">Compro Oro per città</h2>
          <div className="flex flex-wrap gap-2">
            {cities.map(city => (
              <a
                key={city}
                href={`/compro-oro/${city.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-')}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (onCityClick) onCityClick(city);
                }}
                aria-label={`Visualizza compro oro a ${city}`}
                className="px-3 py-1 text-sm rounded-full bg-muted hover:bg-muted-foreground hover:text-background transition-colors cursor-pointer"
              >
                {city}
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg shadow-sm p-4 mb-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <DealersFilter 
              filters={filters}
              onUpdateFilters={onUpdateFilters}
              onRefresh={onRefresh}
              isLoading={isLoading}
            />
            
            <Tabs 
              defaultValue="list" 
              value={view} 
              onValueChange={(v) => onViewChange(v as "list" | "map")} 
              className="w-full sm:w-auto"
            >
              <TabsList className="grid w-full sm:w-[200px] grid-cols-2">
                <TabsTrigger value="list" className="flex items-center gap-2">
                  <List className="h-4 w-4" />
                  Lista
                </TabsTrigger>
                <TabsTrigger value="map" className="flex items-center gap-2">
                  <Map className="h-4 w-4" />
                  Mappa
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {view === "map" ? (
        <div className="space-y-4">
          {/* Mappa compatta */}
          <div className="bg-card rounded-lg shadow-sm p-4">
            <DealersMap dealers={dealers} height="400px" />
          </div>
          
          {/* Lista semplificata sotto la mappa */}
          <div className="bg-card rounded-lg shadow-sm p-4">
            <h3 className="text-lg font-medium mb-4">Compro Oro nelle vicinanze</h3>
            <DealersList 
              dealers={dealers} 
              userPosition={position}
              onDealerSelect={(dealer) => onDealerClick(dealer.id)}
            />
          </div>
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow-sm p-4">
          <DealersListView 
            dealers={dealers} 
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            totalCount={totalCount}
            onDealerClick={onDealerClick}
            onRefresh={onRefresh}
            onLoadMore={onLoadMore}
            enableInfiniteScroll={enableInfiniteScroll}
          />
        </div>
      )}
    </div>
  );
}
